import apiClient from './axios';
import { ApiResponse } from '../types';

/**
 * Compliance console API.
 *
 * Mirrors `admin/compliance/*` and `admin/mfa/*` on the backend. Two things
 * about this surface are deliberate and easy to undo by accident:
 *
 * 1. **Nothing here returns a customer-facing string.** Case summaries and
 *    report narratives are internal records; they must never be rendered into
 *    anything a customer can see. Disclosing to someone that they are under
 *    investigation is an offence under MLPPA s.14.
 * 2. **Every mutating call requires MFA server-side.** `MfaRequiredGuard`
 *    refuses `compliance.*` from an admin without a second factor, so a 403
 *    mentioning multi-factor is an enrolment prompt, not a permissions bug.
 */

export type CaseStatus = 'open' | 'in_review' | 'escalated' | 'closed';
export type CaseSeverity = 'low' | 'medium' | 'high' | 'critical';
export type CaseType =
  | 'sanctions_hit'
  | 'monitoring_alert'
  | 'ctr'
  | 'str'
  | 'edd_review'
  | 'periodic_review';
export type CaseDisposition =
  | 'false_positive'
  | 'no_action'
  | 'edd_applied'
  | 'reported'
  | 'terminated';

export interface ComplianceCase {
  id: string;
  reference: string;
  caseType: CaseType;
  severity: CaseSeverity;
  status: CaseStatus;
  subjectType: string;
  subjectId: string;
  assignedTo: string | null;
  dueAt: string | null;
  raisedBy: string;
  linkedAlertIds: string[];
  linkedReportIds: string[];
  linkedScreeningIds: string[];
  summary: string | null;
  disposition: CaseDisposition | null;
  dispositionReason: string | null;
  closedBy: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CaseStats {
  open: number;
  unassigned: number;
  /** Past SLA. The number an examiner asks for. */
  breached: number;
  bySeverity: Record<string, number>;
}

export interface RegulatoryReport {
  id: string;
  reference: string;
  reportType: 'ctr' | 'str' | 'ftr';
  subjectType: string;
  subjectId: string;
  transactionIds: string[];
  amount: string;
  currency: string;
  triggerReason: string;
  status: 'draft' | 'pending_review' | 'escalated' | 'acknowledged' | 'closed';
  /** When suspicion was formed — the statutory clock starts here, not at draft. */
  detectedAt: string;
  escalatedToPartner: boolean;
  escalatedAt: string | null;
  partnerRef: string | null;
  partnerAcknowledgedAt: string | null;
  narrative: string | null;
  payload: Record<string, unknown> | null;
}

export interface ComplianceThreshold {
  key: string;
  amount: number;
  currency: string;
  description: string | null;
}

export interface ScreeningResult {
  id: string;
  subjectType: string;
  subjectId: string;
  screenedName: string;
  context: string;
  matchScore: number;
  matchedEntryId: string | null;
  status: 'clear' | 'potential_match' | 'confirmed_match' | 'false_positive';
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  createdAt: string;
}

export interface SanctionsEntry {
  id: string;
  entryType: string;
  primaryName: string;
  aliases: string[];
  dateOfBirth: string | null;
  nationality: string | null;
  designationRef: string | null;
  designatedAt: string | null;
}

export interface ReviewItem {
  result: ScreeningResult;
  matched: SanctionsEntry | null;
}

export interface ListHealth {
  name: string;
  source: string;
  version: string | null;
  entryCount: number;
  lastRefreshedAt: string | null;
  isActive: boolean;
  staleDays: number | null;
  stale: boolean;
}

export interface RiskFactor {
  factor: string;
  points: number;
  reason: string;
  floor?: 'low' | 'medium' | 'high';
}

export interface RiskProfile {
  id: string;
  subjectType: string;
  subjectId: string;
  rating: 'low' | 'medium' | 'high';
  score: number;
  factors: RiskFactor[];
  isPep: boolean;
  pepCategory: string | null;
  expectedMonthlyVolume: string | null;
  occupation: string | null;
  lastAssessedAt: string | null;
  nextReviewAt: string | null;
  assessedBy: 'system' | 'manual';
  overriddenBy: string | null;
  overrideReason: string | null;
}

export interface CaseQuery {
  status?: CaseStatus[];
  severity?: CaseSeverity[];
  caseType?: CaseType[];
  assignedTo?: string;
  breachedOnly?: boolean;
  limit?: number;
  offset?: number;
}

const list = (value?: string[]) =>
  value && value.length ? value.join(',') : undefined;

export const complianceApi = {
  listCases: async (query: CaseQuery = {}) => {
    const { data } = await apiClient.get<
      ApiResponse<{ items: ComplianceCase[]; total: number }>
    >('/admin/compliance/cases', {
      params: {
        status: list(query.status),
        severity: list(query.severity),
        caseType: list(query.caseType),
        assignedTo: query.assignedTo,
        breachedOnly: query.breachedOnly ? 'true' : undefined,
        limit: query.limit,
        offset: query.offset,
      },
    });
    return data.data;
  },

  caseStats: async () => {
    const { data } =
      await apiClient.get<ApiResponse<CaseStats>>('/admin/compliance/cases/stats');
    return data.data;
  },

  getCase: async (id: string) => {
    const { data } = await apiClient.get<ApiResponse<ComplianceCase>>(
      `/admin/compliance/cases/${id}`,
    );
    return data.data;
  },

  assignCase: async (id: string, assigneeId: string) => {
    const { data } = await apiClient.post<ApiResponse<ComplianceCase>>(
      `/admin/compliance/cases/${id}/assign`,
      { assigneeId },
    );
    return data.data;
  },

  escalateCase: async (id: string, note: string) => {
    const { data } = await apiClient.post<ApiResponse<ComplianceCase>>(
      `/admin/compliance/cases/${id}/escalate`,
      { note },
    );
    return data.data;
  },

  /**
   * Closing requires a disposition and a reason of at least 20 characters,
   * and the backend refuses a close by whoever raised the case (policy §16).
   * Both rules are enforced server-side; the UI mirrors them to give a better
   * error, never as the enforcement point.
   */
  closeCase: async (
    id: string,
    disposition: CaseDisposition,
    reason: string,
  ) => {
    const { data } = await apiClient.post<ApiResponse<ComplianceCase>>(
      `/admin/compliance/cases/${id}/close`,
      { disposition, reason },
    );
    return data.data;
  },

  outstandingReports: async () => {
    const { data } = await apiClient.get<
      ApiResponse<{ overdue: RegulatoryReport[]; unacknowledged: RegulatoryReport[] }>
    >('/admin/compliance/reports/outstanding');
    return data.data;
  },

  escalateReport: async (id: string, partnerRef?: string) => {
    const { data } = await apiClient.post<ApiResponse<RegulatoryReport>>(
      `/admin/compliance/reports/${id}/escalate`,
      { partnerRef },
    );
    return data.data;
  },

  acknowledgeReport: async (id: string, partnerRef: string) => {
    const { data } = await apiClient.post<ApiResponse<RegulatoryReport>>(
      `/admin/compliance/reports/${id}/acknowledge`,
      { partnerRef },
    );
    return data.data;
  },

  pendingScreening: async (limit = 50, offset = 0) => {
    const { data } = await apiClient.get<
      ApiResponse<{ items: ReviewItem[]; total: number }>
    >('/admin/compliance/screening/pending', { params: { limit, offset } });
    return data.data;
  },

  getScreening: async (id: string) => {
    const { data } = await apiClient.get<ApiResponse<ReviewItem>>(
      `/admin/compliance/screening/${id}`,
    );
    return data.data;
  },

  /** Records a false positive. Resolves this screen only, not the subject. */
  clearScreening: async (id: string, reason: string) => {
    const { data } = await apiClient.post<ApiResponse<ScreeningResult>>(
      `/admin/compliance/screening/${id}/clear`,
      { reason },
    );
    return data.data;
  },

  /**
   * Confirms a match. Freezes the subject immediately without notice to them
   * (TPPA s.54), escalates the case to CRITICAL and drafts an STR against a
   * 24-hour clock. Requires `compliance.close`.
   */
  confirmScreening: async (id: string) => {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      `/admin/compliance/screening/${id}/confirm`,
    );
    return data.data;
  },

  listHealth: async () => {
    const { data } = await apiClient.get<ApiResponse<ListHealth[]>>(
      '/admin/compliance/lists/health',
    );
    return data.data;
  },

  riskProfile: async (subjectType: string, subjectId: string) => {
    const { data } = await apiClient.get<ApiResponse<RiskProfile | null>>(
      `/admin/compliance/risk/${subjectType}/${subjectId}`,
    );
    return data.data;
  },

  overrideRisk: async (
    subjectType: string,
    subjectId: string,
    rating: 'low' | 'medium' | 'high',
    reason: string,
  ) => {
    const { data } = await apiClient.post<ApiResponse<RiskProfile>>(
      `/admin/compliance/risk/${subjectType}/${subjectId}/override`,
      { rating, reason },
    );
    return data.data;
  },

  listThresholds: async () => {
    const { data } = await apiClient.get<ApiResponse<ComplianceThreshold[]>>(
      '/admin/compliance/thresholds',
    );
    return data.data;
  },

  updateThreshold: async (key: string, amount: number, reason: string) => {
    const { data } = await apiClient.post<ApiResponse<ComplianceThreshold>>(
      '/admin/compliance/thresholds',
      { key, amount, reason },
    );
    return data.data;
  },
};

// ─── MFA ─────────────────────────────────────────────────────────────────────

export interface MfaEnrolment {
  secret: string;
  /** otpauth:// URI for the authenticator app. */
  uri: string;
}

export const mfaApi = {
  status: async () => {
    const { data } =
      await apiClient.get<ApiResponse<{ enrolled: boolean }>>('/admin/mfa/status');
    return data.data;
  },

  enrol: async () => {
    const { data } =
      await apiClient.post<ApiResponse<MfaEnrolment>>('/admin/mfa/enrol');
    return data.data;
  },

  /** Returns the recovery codes **once**. They cannot be retrieved again. */
  confirm: async (token: string) => {
    const { data } = await apiClient.post<
      ApiResponse<{ recoveryCodes: string[]; notice: string }>
    >('/admin/mfa/confirm', { token });
    return data.data;
  },

  reset: async (token: string) => {
    const { data } = await apiClient.post<ApiResponse<{ enrolled: boolean }>>(
      '/admin/mfa/reset',
      { token },
    );
    return data.data;
  },
};
